import React, { useState } from 'react';
import { Download, Clock, AlertCircle, RefreshCw, CheckCircle2, Loader2 } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL;

// One purchased file: Download button + remaining downloads / expiry, with a
// self-service "Request new link" when it expires or runs out.
export const DownloadFileCard = function(props) {
  var file = props.file;
  var libraryKey = props.libraryKey;
  var [state, setState] = useState('idle');
  var [msg, setMsg] = useState('');

  function startDownload() {
    window.location.assign(API + file.download_url);
  }

  async function requestNewLink() {
    setState('sending'); setMsg('');
    try {
      var body = libraryKey ? { key: libraryKey, slug: file.slug } : { token: file.token };
      var res = await fetch(API + '/api/download/reissue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not create a new link');
      setState('sent');
      setMsg('A fresh link is on its way to ' + (data.emailed_to || 'your email') + '.');
    } catch (e) {
      setState('error');
      setMsg(e.message);
    }
  }

  var expiresLabel = file.expires_at ? new Date(file.expires_at).toLocaleDateString() : null;

  return (
    <div
      data-testid={'download-card-' + file.slug}
      className="border border-white/10 bg-neutral-900/70 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex-1 min-w-0">
        <h3 className={'text-base md:text-lg font-semibold leading-snug ' +
          (file.platform === 'mobilesheets' ? 'text-emerald-400' : file.platform === 'forscore' ? 'text-sky-400' : 'text-white')}>
          {file.name}
          {file.platform ? (
            <span data-testid={'download-platform-' + file.slug} className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider">
              ({file.platform === 'mobilesheets' ? 'MobileSheets' : 'forScore'})
            </span>
          ) : null}
        </h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-neutral-500">
          {file.order_number ? <span>Order {file.order_number}</span> : null}
          {file.available ? (
            <span className="flex items-center gap-1">
              <Download className="w-3 h-3" />
              {file.downloads_left} of {file.max_downloads} downloads left
            </span>
          ) : null}
          {expiresLabel ? (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {file.expired ? 'Expired ' + expiresLabel : 'Valid until ' + expiresLabel}
            </span>
          ) : null}
        </div>
        {!file.file_ready ? (
          <p className="flex items-center gap-1.5 text-amber-400/90 text-xs mt-2">
            <AlertCircle className="w-3 h-3" />
            This file is being prepared — reply to your order email and I'll send it straight over.
          </p>
        ) : null}
        {msg ? (
          <p data-testid={'download-msg-' + file.slug} className={'text-xs mt-2 ' + (state === 'error' ? 'text-red-400' : 'text-emerald-400')}>
            {msg}
          </p>
        ) : null}
      </div>

      <div className="flex-shrink-0">
        {file.available && file.file_ready ? (
          <button
            onClick={startDownload}
            data-testid={'download-btn-' + file.slug}
            className="inline-flex items-center gap-2 bg-[#d4af37] hover:bg-white text-black font-semibold text-sm rounded-full px-6 py-3 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        ) : state === 'sent' ? (
          <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-semibold">
            <CheckCircle2 className="w-4 h-4" /> Link sent
          </span>
        ) : (
          <button
            onClick={requestNewLink}
            disabled={state === 'sending'}
            data-testid={'download-renew-btn-' + file.slug}
            className="inline-flex items-center gap-2 border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-black font-semibold text-sm rounded-full px-5 py-3 transition-colors disabled:opacity-60"
          >
            {state === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {state === 'sending' ? 'Sending…' : 'Request new link'}
          </button>
        )}
      </div>
    </div>
  );
};

// Full list used on the confirmation page and in the customer's library.
export const DownloadList = function(props) {
  var files = props.files || [];
  if (files.length === 0) return null;
  return (
    <div data-testid="download-list" className="space-y-3">
      {files.map(function(f) {
        return <DownloadFileCard key={f.token || f.slug} file={f} libraryKey={props.libraryKey} />;
      })}
    </div>
  );
};

export default DownloadList;
